import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
    WaterDistributionZone,
    WaterDistributionZoneData,
    WaterSource,
    WaterSourceData,
    Reservoir,
    ReservoirDMA,
    ReservoirDMAData,
    ReservoirData,
    PipelineDMA,
    PipelineDMAData,
    PipelineSegment,
    PipelineSegmentData,
    TreatmentPlant,
    TreatmentPlantData,
    Valve,
    ValveDMA,
    ValveDMAData,
    ValveData,
    SourceState
} from "@/types";
import axios, { AxiosError, AxiosHeaderValue } from 'axios';
import {
    waterSourcesURL,
    deletedWaterSourcesURL,
    createWaterSourceURL,
    updateWaterSourceURL,
    deleteWaterSourceURL,
    activateWaterSourceURL,
    waterDistributionZonesURL,
    deletedWaterDistributionZonesURL,
    createWaterDistributionZoneURL,
    updateWaterDistributionZoneURL,
    deleteWaterDistributionZoneURL,
    activateWaterDistributionZoneURL,
    treatmentPlantsURL,
    deletedTreatmentPlantsURL,
    createTreatmentPlantURL,
    updateTreatmentPlantURL,
    deleteTreatmentPlantURL,
    activateTreatmentPlantURL,
    reservoirsURL,
    deletedReservoirsURL,
    createReservoirURL,
    updateReservoirURL,
    deleteReservoirURL,
    activateReservoirURL,
    reservoirDMAsURL,
    deletedReservoirDMAsURL,
    createReservoirDMAURL,
    updateReservoirDMAURL,
    deleteReservoirDMAURL,
    activateReservoirDMAURL,
    pipelineDMAsURL,
    deletedPipelineDMAsURL,
    createPipelineDMAURL,
    updatePipelineDMAURL,
    deletePipelineDMAURL,
    activatePipelineDMAURL,
    pipelineSegmentsURL,
    deletedPipelineSegmentsURL,
    createPipelineSegmentURL,
    updatePipelineSegmentURL,
    deletePipelineSegmentURL,
    activatePipelineSegmentURL,
    valveDMAsURL,
    deletedValveDMAsURL,
    createValveDMAURL,
    updateValveDMAURL,
    deleteValveDMAURL,
    activateValveDMAURL,
    valvesURL,
    deletedValvesURL,
    createValveURL,
    updateValveURL,
    deleteValveURL,
    activateValveURL
} from "../../constants";
import {destroyToten} from '../authentications/authenticationsSlice'


interface ErrorMessage {
  error: string;
  message: string;
}



interface getSourcesArgsType {
  token: string | null;
}

interface getThingArgsType {
  token: string | null;
  payload:{thingId: string | undefined| null}
}

interface createWaterSourceDataType {
  payload: WaterSourceData;
  token: string | null;
}

interface updateWaterSourceDataType {
  payload: WaterSource;
  token: string | null;
}

interface createWaterDistributionZoneDataType {
  payload: WaterDistributionZoneData;
  token: string | null;
}

interface updateWaterDistributionZoneDataType {
  payload: WaterDistributionZone;
  token: string | null;
}

interface createReservoirDataType {
  payload: ReservoirData;
  token: string | null;
}

interface updateReservoirDataType {
  payload: Reservoir;
  token: string | null;
}

interface createReservoirDMADataType {
  payload: ReservoirDMAData;
  token: string | null;
}

interface updateReservoirDMADataType {
  payload: ReservoirDMA;
  token: string | null;
}

interface createPipelineDMADataType {
  payload: PipelineDMAData;
  token: string | null;
}

interface updatePipelineDMADataType {
  payload: PipelineDMA;
  token: string | null;
}

interface createPipelineSegmentDataType {
  payload: PipelineSegmentData;
  token: string | null;
}

interface updatePipelineSegmentDataType {
  payload: PipelineSegment;
  token: string | null;
}

interface createTreatmentPlantDataType {
  payload: TreatmentPlantData;
  token: string | null;
}

interface updateTreatmentPlantDataType {
  payload: TreatmentPlant;
  token: string | null;
}

interface createValveDataType {
  payload: ValveData;
  token: string | null;
}

interface updateValveDataType {
  payload: Valve;
  token: string | null;
}

interface createValveDMADataType {
  payload: ValveDMAData;
  token: string | null;
}

interface updateValveDMADataType {
  payload: ValveDMA;
  token: string | null;
}

interface createValveDMADataType {
  payload: ValveDMAData;
  token: string | null;
}

interface updateValveDMADataType {
  payload: ValveDMA;
  token: string | null;
}


export const waterDistributionZoneInitialValues: WaterDistributionZoneData = {
    suburbId :"",
    suburb :"",
    boundary: {
        type: "Polygon",
        coordinates: [[]] // Default empty polygon coordinates
    },
    dmaId :"",
    dma :"",
    demandProfile :"",
    averagePressureBar: 0.0,
    riskScore: 0.0,
    status :"",
}

export const waterSourceInitialValues: WaterSourceData = {
    name:"",
    type:"",
    longitude: 0,
    latitude: 0,
    capacityM3: 0,
    currentLevelM3: 0,
    status:"",
    owner:"",
    ownerId:"",
}


export const reservoirInitialValues: ReservoirData = {
    name: "",
    type: "",
    latitude: 0,
    longitude: 0,
    storageCapacityM3: 0,
    status: "",
    zone: "",
    zoneId: "",
    localAuthorityId: "",
    localAuthority: "",
    connectedSourceId: "",
    connectedSource: "",
    connectedTreatmentPlantId: "",
    connectedTreatmentPlant: "",
}


export const reservoirDMAInitialValues: ReservoirDMAData = {
    reservoirId: "",
    reservoir: "",
    dmaId: "",
    dma: "",
    supplyCapacityM3Day: 0.0,
    isPrimarySource: false,
}

export const pipelineDMAInitialValues: PipelineDMAData = {
    pipelineId:"",
    pipeline:"",
    dmaId:"",
    dma:"",
    role:"",
    direction:"",
}

export const pipelineSegmentInitialValues: PipelineSegmentData = {
    name: "",
    fromNode: "",
    toNode: "",
    fromNodeLatitude: "",
    fromNodeLongitude: "",
    toNodeLatitude: 0,
    toNodeLongitude: 0,
    fromNodeType: "",
    toNodeType: "",
    material: "",
    diameterMm: 0,
    lengthM: 0,
    pressureRatingBar: 0,
    installDate: "",
    status: "",
}

export const treatmentPlantInitialValues: TreatmentPlantData = {
    name: "",
    latitude: 0,
    longitude: 0,
    capacityM3Day: 0,
    energySource: "",
    status: "",
    localAuthorityId: "",
    localAuthority: "",
    sourceId: "",
    source: "",
}

export const valveInitialValues: ValveData = {
    name :"",
    type :"",
    longitude: 0,
    latitude: 0,
    mountedOnId :"",
    mountedOnType :"",
    status :"",
    controlMode :"",
    pressureRatingBar: 0,
}

export const valveDMAInitialValues: ValveDMAData = {
    valveId: "",
    valve: "",
    mountedOnId: "",
    mountedOnType: "",
    controlType: "",
    defaultStatus: "",
}


export const initialState: SourceState = {
    getStatus: 'idle',
    createSourceStatus: 'idle',
    error: null,
    message: null,
    waterDistributionZone: {
        id: "",
        suburbId :"",
        suburb :"",
        boundary: {
            type: "Polygon",
            coordinates: [[]] // Default empty polygon coordinates
        },
        dmaId :"",
        dma :"",
        demandProfile :"",
        averagePressureBar: 0.0,
        riskScore: 0.0,
        status :"",
    },
    waterDistributionZones: [],
    waterSource: {
        id: "",
        name:"",
        type:"",
        longitude: 0,
        latitude: 0,
        capacityM3: 0,
        currentLevelM3: 0,
        status:"",
        owner:"",
        ownerId:"",
    },
    waterSources: [],
    reservoir: {
        id: "",
        name: "",
        type: "",
        latitude: 0,
        longitude: 0,
        storageCapacityM3: 0,
        status: "",
        zone: "",
        zoneId: "",
        localAuthorityId: "",
        localAuthority: "",
        connectedSourceId: "",
        connectedSource: "",
        connectedTreatmentPlantId: "",
        connectedTreatmentPlant: "",
    },
    reservoirs: [],
    reservoirDMA : {
        id: "",
        reservoirId: "",
        reservoir: "",
        dmaId: "",
        dma: "",
        supplyCapacityM3Day: 0.0,
        isPrimarySource: false,
    },
    reservoirDMAs: [],
    pipelineDMA: {
        id: "",
        pipelineId:"",
        pipeline:"",
        dmaId:"",
        dma:"",
        role:"",
        direction:"",
    },
    pipelineDMAs: [],
    pipelineSegment: {
        id: "",
        name: "",
        fromNode: "",
        toNode: "",
        fromNodeLatitude: "",
        fromNodeLongitude: "",
        toNodeLatitude: 0,
        toNodeLongitude: 0,
        fromNodeType: "",
        toNodeType: "",
        material: "",
        diameterMm: 0,
        lengthM: 0,
        pressureRatingBar: 0,
        installDate: "",
        status: "",
        lastInspected: "",
    },
    pipelineSegments: [],
    treatmentPlant: {
        id: "",
        name: "",
        latitude: 0,
        longitude: 0,
        capacityM3Day: 0,
        energySource: "",
        status: "",
        localAuthorityId: "",
        localAuthority: "",
        sourceId: "",
        source: "",
    },
    treatmentPlants: [],
    valve: {
        id: "",
        name :"",
        type :"",
        longitude: 0,
        latitude: 0,
        mountedOnId :"",
        mountedOnType :"",
        status :"",
        controlMode :"",
        pressureRatingBar: 0,
    },
    valves: [],
    valveDMA: {
        id: "",
        valveId: "",
        valve: "",
        mountedOnId: "",
        mountedOnType: "",
        controlType: "",
        defaultStatus: "",
    },
    valveDMAs: [],
}


export const createWaterSource = createAsyncThunk<WaterSource, createWaterSourceDataType, { rejectValue: ErrorMessage }>(
  "sources/createWaterSource",
  async (data:createWaterSourceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createWaterSourceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateWaterSource = createAsyncThunk<WaterSource, updateWaterSourceDataType, { rejectValue: ErrorMessage }>(
  "sources/updateWaterSource",
  async (data:updateWaterSourceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateWaterSourceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteWaterSource = createAsyncThunk<WaterSource, updateWaterSourceDataType, { rejectValue: ErrorMessage }>(
  "sources/deleteWaterSource",
  async (data:updateWaterSourceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteWaterSourceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateWaterSource = createAsyncThunk<WaterSource, updateWaterSourceDataType, { rejectValue: ErrorMessage }>(
  "sources/activateWaterSource",
  async (data:updateWaterSourceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateWaterSourceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedWaterSources = createAsyncThunk<WaterSource[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedWaterSources",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedWaterSourcesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getWaterSources = createAsyncThunk<WaterSource[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getWaterSources",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(waterSourcesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getWaterSource = createAsyncThunk<WaterSource, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getWaterSource",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${waterSourcesURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createWaterDistributionZone = createAsyncThunk<WaterDistributionZone, createWaterDistributionZoneDataType, { rejectValue: ErrorMessage }>(
  "sources/createWaterDistributionZone",
  async (data:createWaterDistributionZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createWaterDistributionZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateWaterDistributionZone = createAsyncThunk<WaterDistributionZone, updateWaterDistributionZoneDataType, { rejectValue: ErrorMessage }>(
  "sources/updateWaterDistributionZone",
  async (data:updateWaterDistributionZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateWaterDistributionZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteWaterDistributionZone = createAsyncThunk<WaterDistributionZone, updateWaterDistributionZoneDataType, { rejectValue: ErrorMessage }>(
  "sources/deleteWaterDistributionZone",
  async (data:updateWaterDistributionZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteWaterDistributionZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateWaterDistributionZone = createAsyncThunk<WaterDistributionZone, updateWaterDistributionZoneDataType, { rejectValue: ErrorMessage }>(
  "sources/activateWaterDistributionZone",
  async (data:updateWaterDistributionZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateWaterDistributionZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedWaterDistributionZones = createAsyncThunk<WaterDistributionZone[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedWaterDistributionZones",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedWaterDistributionZonesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getWaterDistributionZones = createAsyncThunk<WaterDistributionZone[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getWaterDistributionZones",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(waterDistributionZonesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getWaterDistributionZone = createAsyncThunk<WaterDistributionZone, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getWaterDistributionZone",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${waterDistributionZonesURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const createReservoir = createAsyncThunk<Reservoir, createReservoirDataType, { rejectValue: ErrorMessage }>(
  "sources/createReservoir",
  async (data:createReservoirDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createReservoirURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateReservoir = createAsyncThunk<Reservoir, updateReservoirDataType, { rejectValue: ErrorMessage }>(
  "sources/updateReservoir",
  async (data:updateReservoirDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateReservoirURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteReservoir = createAsyncThunk<Reservoir, updateReservoirDataType, { rejectValue: ErrorMessage }>(
  "sources/deleteReservoir",
  async (data:updateReservoirDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteReservoirURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateReservoir = createAsyncThunk<Reservoir, updateReservoirDataType, { rejectValue: ErrorMessage }>(
  "sources/activateReservoir",
  async (data:updateReservoirDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateReservoirURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedReservoirs = createAsyncThunk<Reservoir[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedReservoirs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedReservoirsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getReservoirs = createAsyncThunk<Reservoir[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getReservoirs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(reservoirsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getReservoir = createAsyncThunk<Reservoir, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getReservoir",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${reservoirsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//

export const createReservoirDMA = createAsyncThunk<ReservoirDMA, createReservoirDMADataType, { rejectValue: ErrorMessage }>(
  "sources/createReservoirDMA",
  async (data:createReservoirDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createReservoirDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateReservoirDMA = createAsyncThunk<ReservoirDMA, updateReservoirDMADataType, { rejectValue: ErrorMessage }>(
  "sources/updateReservoirDMA",
  async (data:updateReservoirDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateReservoirDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteReservoirDMA = createAsyncThunk<ReservoirDMA, updateReservoirDMADataType, { rejectValue: ErrorMessage }>(
  "sources/deleteReservoirDMA",
  async (data:updateReservoirDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteReservoirDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateReservoirDMA = createAsyncThunk<ReservoirDMA, updateReservoirDMADataType, { rejectValue: ErrorMessage }>(
  "sources/activateReservoirDMA",
  async (data:updateReservoirDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateReservoirDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedReservoirDMAs = createAsyncThunk<ReservoirDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedReservoirDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedReservoirDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getReservoirDMAs = createAsyncThunk<ReservoirDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getReservoirDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(reservoirDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getReservoirDMA = createAsyncThunk<ReservoirDMA, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getReservoirDMA",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${reservoirDMAsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//

export const createPipelineDMA = createAsyncThunk<PipelineDMA, createPipelineDMADataType, { rejectValue: ErrorMessage }>(
  "sources/createPipelineDMA",
  async (data:createPipelineDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createPipelineDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updatePipelineDMA = createAsyncThunk<PipelineDMA, updatePipelineDMADataType, { rejectValue: ErrorMessage }>(
  "sources/updatePipelineDMA",
  async (data:updatePipelineDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updatePipelineDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deletePipelineDMA = createAsyncThunk<PipelineDMA, updatePipelineDMADataType, { rejectValue: ErrorMessage }>(
  "sources/deletePipelineDMA",
  async (data:updatePipelineDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deletePipelineDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activatePipelineDMA = createAsyncThunk<PipelineDMA, updatePipelineDMADataType, { rejectValue: ErrorMessage }>(
  "sources/activatePipelineDMA",
  async (data:updatePipelineDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activatePipelineDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedPipelineDMAs = createAsyncThunk<PipelineDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedPipelineDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedPipelineDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getPipelineDMAs = createAsyncThunk<PipelineDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getPipelineDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(pipelineDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getPipelineDMA = createAsyncThunk<PipelineDMA, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getPipelineDMA",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${pipelineDMAsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//

export const createPipelineSegment = createAsyncThunk<PipelineSegment, createPipelineSegmentDataType, { rejectValue: ErrorMessage }>(
  "sources/createPipelineSegment",
  async (data:createPipelineSegmentDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createPipelineSegmentURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updatePipelineSegment = createAsyncThunk<PipelineSegment, updatePipelineSegmentDataType, { rejectValue: ErrorMessage }>(
  "sources/updatePipelineSegment",
  async (data:updatePipelineSegmentDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updatePipelineSegmentURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deletePipelineSegment = createAsyncThunk<PipelineSegment, updatePipelineSegmentDataType, { rejectValue: ErrorMessage }>(
  "sources/deletePipelineSegment",
  async (data:updatePipelineSegmentDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deletePipelineSegmentURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activatePipelineSegment = createAsyncThunk<PipelineSegment, updatePipelineSegmentDataType, { rejectValue: ErrorMessage }>(
  "sources/activatePipelineSegment",
  async (data:updatePipelineSegmentDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activatePipelineSegmentURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedPipelineSegments = createAsyncThunk<PipelineSegment[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedPipelineSegments",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedPipelineSegmentsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getPipelineSegments = createAsyncThunk<PipelineSegment[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getPipelineSegments",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(pipelineSegmentsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getPipelineSegment = createAsyncThunk<PipelineSegment, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getPipelineSegment",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${pipelineSegmentsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


//

export const createTreatmentPlant = createAsyncThunk<TreatmentPlant, createTreatmentPlantDataType, { rejectValue: ErrorMessage }>(
  "sources/createTreatmentPlant",
  async (data:createTreatmentPlantDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createTreatmentPlantURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateTreatmentPlant = createAsyncThunk<TreatmentPlant, updateTreatmentPlantDataType, { rejectValue: ErrorMessage }>(
  "sources/updateTreatmentPlant",
  async (data:updateTreatmentPlantDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateTreatmentPlantURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteTreatmentPlant = createAsyncThunk<TreatmentPlant, updateTreatmentPlantDataType, { rejectValue: ErrorMessage }>(
  "sources/deleteTreatmentPlant",
  async (data:updateTreatmentPlantDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteTreatmentPlantURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateTreatmentPlant = createAsyncThunk<TreatmentPlant, updateTreatmentPlantDataType, { rejectValue: ErrorMessage }>(
  "sources/activateTreatmentPlant",
  async (data:updateTreatmentPlantDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateTreatmentPlantURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedTreatmentPlants = createAsyncThunk<TreatmentPlant[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedTreatmentPlants",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedTreatmentPlantsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getTreatmentPlants = createAsyncThunk<TreatmentPlant[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getTreatmentPlants",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(treatmentPlantsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getTreatmentPlant = createAsyncThunk<TreatmentPlant, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getTreatmentPlant",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${treatmentPlantsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//

export const createValve = createAsyncThunk<Valve, createValveDataType, { rejectValue: ErrorMessage }>(
  "sources/createValve",
  async (data:createValveDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createValveURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateValve = createAsyncThunk<Valve, updateValveDataType, { rejectValue: ErrorMessage }>(
  "sources/updateValve",
  async (data:updateValveDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateValveURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteValve = createAsyncThunk<Valve, updateValveDataType, { rejectValue: ErrorMessage }>(
  "sources/deleteValve",
  async (data:updateValveDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteValveURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateValve = createAsyncThunk<Valve, updateValveDataType, { rejectValue: ErrorMessage }>(
  "sources/activateValve",
  async (data:updateValveDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateValveURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedValves = createAsyncThunk<Valve[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedValves",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedValvesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getValves = createAsyncThunk<Valve[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getValves",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(valvesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getValve = createAsyncThunk<Valve, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getValve",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${valvesURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


//

export const createValveDMA = createAsyncThunk<ValveDMA, createValveDMADataType, { rejectValue: ErrorMessage }>(
  "sources/createValveDMA",
  async (data:createValveDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createValveDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateValveDMA = createAsyncThunk<ValveDMA, updateValveDMADataType, { rejectValue: ErrorMessage }>(
  "sources/updateValveDMA",
  async (data:updateValveDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateValveDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteValveDMA = createAsyncThunk<ValveDMA, updateValveDMADataType, { rejectValue: ErrorMessage }>(
  "sources/deleteValveDMA",
  async (data:updateValveDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteValveDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateValveDMA = createAsyncThunk<ValveDMA, updateValveDMADataType, { rejectValue: ErrorMessage }>(
  "sources/activateValveDMA",
  async (data:updateValveDMADataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateValveDMAURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedValveDMAs = createAsyncThunk<ValveDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getDeletedValveDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedValveDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getValveDMAs = createAsyncThunk<ValveDMA[], getSourcesArgsType, { rejectValue: ErrorMessage }>(
  "sources/getValveDMAs",
  async (data: getSourcesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(valveDMAsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getValveDMA = createAsyncThunk<ValveDMA, getThingArgsType, { rejectValue: ErrorMessage }>(
  "sources/getValveDMA",
  async (data: getThingArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let thingId = data?.payload.thingId
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(`${valveDMAsURL}${thingId}/`);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);




export const sourcesSlice = createSlice({
  name: 'sources',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWaterSources.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterSources.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterSources = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterSources.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterSources = [];
      })
      .addCase(getWaterSource.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterSource.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterSource = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterSource.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterSource = initialState.waterSource;
      })
      .addCase(getDeletedWaterSources.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedWaterSources.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterSources = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedWaterSources.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterSources = [];
      })
      .addCase(createWaterSource.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createWaterSource.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createWaterSource.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateWaterSource.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateWaterSource.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterSource Created";
      })
      .addCase(updateWaterSource.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteWaterSource.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteWaterSource.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterSource Created";
      })
      .addCase(deleteWaterSource.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateWaterSource.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateWaterSource.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterSource Created";
      })
      .addCase(activateWaterSource.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      .addCase(getWaterDistributionZones.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterDistributionZones.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterDistributionZones = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterDistributionZones.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterDistributionZones = [];
      })
      .addCase(getWaterDistributionZone.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterDistributionZone.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterDistributionZone = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getWaterDistributionZone.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterDistributionZone = initialState.waterDistributionZone;
      })
      .addCase(getDeletedWaterDistributionZones.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedWaterDistributionZones.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.waterDistributionZones = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedWaterDistributionZones.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.waterDistributionZones = [];
      })
      .addCase(createWaterDistributionZone.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createWaterDistributionZone.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createWaterDistributionZone.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateWaterDistributionZone.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateWaterDistributionZone.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterDistributionZone Created";
      })
      .addCase(updateWaterDistributionZone.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteWaterDistributionZone.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteWaterDistributionZone.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterDistributionZone Created";
      })
      .addCase(deleteWaterDistributionZone.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateWaterDistributionZone.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateWaterDistributionZone.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "WaterDistributionZone Created";
      })
      .addCase(activateWaterDistributionZone.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getReservoirs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirs = [];
      })
      .addCase(getReservoir.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoir.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoir = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoir.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoir = initialState.reservoir;
      })
      .addCase(getDeletedReservoirs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirs = [];
      })
      .addCase(createReservoir.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createReservoir.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createReservoir.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateReservoir.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateReservoir.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Created";
      })
      .addCase(updateReservoir.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteReservoir.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteReservoir.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Created";
      })
      .addCase(deleteReservoir.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateReservoir.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateReservoir.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Created";
      })
      .addCase(activateReservoir.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getReservoirDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirDMAs = [];
      })
      .addCase(getReservoirDMA.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirDMA.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirDMA = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirDMA.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirDMA = initialState.reservoirDMA;
      })
      .addCase(getDeletedReservoirDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirDMAs = [];
      })
      .addCase(createReservoirDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createReservoirDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createReservoirDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateReservoirDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateReservoirDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ReservoirDMA Created";
      })
      .addCase(updateReservoirDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteReservoirDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteReservoirDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ReservoirDMA Created";
      })
      .addCase(deleteReservoirDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateReservoirDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateReservoirDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ReservoirDMA Created";
      })
      .addCase(activateReservoirDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getPipelineDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineDMAs = [];
      })
      .addCase(getPipelineDMA.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineDMA.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineDMA = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineDMA.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineDMA = initialState.pipelineDMA;
      })
      .addCase(getDeletedPipelineDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedPipelineDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedPipelineDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineDMAs = [];
      })
      .addCase(createPipelineDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createPipelineDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createPipelineDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updatePipelineDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updatePipelineDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineDMA Created";
      })
      .addCase(updatePipelineDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deletePipelineDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deletePipelineDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineDMA Created";
      })
      .addCase(deletePipelineDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activatePipelineDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activatePipelineDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineDMA Created";
      })
      .addCase(activatePipelineDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getPipelineSegments.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineSegments.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineSegments = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineSegments.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineSegments = [];
      })
      .addCase(getPipelineSegment.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineSegment.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineSegment = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getPipelineSegment.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineSegment = initialState.pipelineSegment;
      })
      .addCase(getDeletedPipelineSegments.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedPipelineSegments.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.pipelineSegments = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedPipelineSegments.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.pipelineSegments = [];
      })
      .addCase(createPipelineSegment.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createPipelineSegment.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createPipelineSegment.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updatePipelineSegment.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updatePipelineSegment.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineSegment Created";
      })
      .addCase(updatePipelineSegment.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deletePipelineSegment.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deletePipelineSegment.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineSegment Created";
      })
      .addCase(deletePipelineSegment.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activatePipelineSegment.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activatePipelineSegment.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "PipelineSegment Created";
      })
      .addCase(activatePipelineSegment.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getTreatmentPlants.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getTreatmentPlants.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.treatmentPlants = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getTreatmentPlants.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.treatmentPlants = [];
      })
      .addCase(getTreatmentPlant.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getTreatmentPlant.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.treatmentPlant = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getTreatmentPlant.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.treatmentPlant = initialState.treatmentPlant;
      })
      .addCase(getDeletedTreatmentPlants.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedTreatmentPlants.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.treatmentPlants = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedTreatmentPlants.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.treatmentPlants = [];
      })
      .addCase(createTreatmentPlant.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createTreatmentPlant.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createTreatmentPlant.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateTreatmentPlant.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateTreatmentPlant.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "TreatmentPlant Created";
      })
      .addCase(updateTreatmentPlant.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteTreatmentPlant.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteTreatmentPlant.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "TreatmentPlant Created";
      })
      .addCase(deleteTreatmentPlant.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateTreatmentPlant.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateTreatmentPlant.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "TreatmentPlant Created";
      })
      .addCase(activateTreatmentPlant.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getValves.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getValves.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valves = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getValves.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valves = [];
      })
      .addCase(getValve.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getValve.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valve = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getValve.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valve = initialState.valve;
      })
      .addCase(getDeletedValves.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedValves.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valves = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedValves.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valves = [];
      })
      .addCase(createValve.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createValve.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createValve.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateValve.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateValve.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Valve Created";
      })
      .addCase(updateValve.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteValve.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteValve.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Valve Created";
      })
      .addCase(deleteValve.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateValve.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateValve.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Valve Created";
      })
      .addCase(activateValve.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getValveDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getValveDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valveDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getValveDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valveDMAs = [];
      })
      .addCase(getValveDMA.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getValveDMA.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valveDMA = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getValveDMA.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valveDMA = initialState.valveDMA;
      })
      .addCase(getDeletedValveDMAs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedValveDMAs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.valveDMAs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedValveDMAs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.valveDMAs = [];
      })
      .addCase(createValveDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createValveDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "Place Created";
      })
      .addCase(createValveDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateValveDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateValveDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ValveDMA Created";
      })
      .addCase(updateValveDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteValveDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteValveDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ValveDMA Created";
      })
      .addCase(deleteValveDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateValveDMA.pending, (state) => {
        state.createSourceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateValveDMA.fulfilled, (state, action) => {
        state.createSourceStatus = 'succeeded';
        state.error = null;
        state.message = "ValveDMA Created";
      })
      .addCase(activateValveDMA.rejected, (state, action) => {
        state.createSourceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })
    }
})

export default sourcesSlice.reducer
